import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { message } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import { useStore } from 'components/Store';
import usePlans from 'hooks/usePlans';
import useListViewSelection from 'hooks/useListViewSelection';
import useDeepLinkSelection from 'hooks/useDeepLinkSelection';
import useDebounce from 'hooks/useDebounce';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import PlansRecordIcon from 'assets/PlansRecordIcon';
import EmptyTableState from 'components/EmptyTableState';
import LoadingSpinner from 'components/LoadingSpinner';
import SpinnerModal from 'components/SpinnerModal';
import NoPlansFoundIcon from 'assets/Illustration Small Empty.svg';
import NoCropZonesFoundIcon from 'assets/properties-map-icon.svg';
import PlusIcon from 'assets/plus.svg?react';
import ListView from 'components/RecordsListView/ListView';
import { SummaryParamsProvider } from 'components/RecordsListView/context/SummaryParamsContext';
import { paths } from 'routes/paths';
import { FILTER_LABEL } from 'screens/Applications&WorkOrders/Components/Constants';
import { buildLandingPage } from 'screens/Applications&WorkOrders/Components/helpers';
import { RECORD_SOURCE_MESSAGE_KEY } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/constants/messageKey';
import { dateRangeFilter } from 'screens/Plans/helpers/dataHelpers';
import usePlanEllipsisOptions from 'screens/Plans/hooks/usePlanEllipsisOptions';
import { setPrintListenerReady } from 'components/Notification/genericNotification';
import useDismissNotificationOnNavigation from 'components/Notification/useDismissNotificationOnNavigation';
import { dismissPlanNotification } from './components/Notification';
import { registerPlanPrinter } from './components/Notification/planPrintBridge';
import PlanDetailPanel from './components/PlanDetailPanel';
import PlanFilterDrawer, {
  PLAN_FILTER_DEFAULTS
} from './components/PlanFilterDrawer';
import PlanSortDropdown, {
  persistPlanSort,
  readPersistedPlanSort
} from './components/PlanSortDropdown';
import './PlansPage.css';
import usePlanListViewColumns from './hooks/usePlanListViewColumns';
import { PlanDetails } from './components/PlanDetailPanel.helpers';
import { PlanFilters, PlanListSummary } from './types';

const PAGE_SIZE = 50;

/**
 * Smart list body: usePlans() server-side pagination + client-side
 * search/sort, feeding the shared <ListView>/<PlanDetailPanel> shell.
 * Seasons/Farms/Crops/Flags stays removed (FR-022, non-functional
 * legacy dropdown). Creation Date filtering (FR-031) is client-side
 * only, filtering the current page via legacy's dateRangeFilter —
 * never sent to the API.
 */
const PlansListView: React.FC<{ onCreateClick: () => void }> = ({
  onCreateClick
}) => {
  const history = useHistory();
  const location = useLocation();
  const {
    plans,
    plansCount,
    plansNameQuery,
    pageNo,
    goToPage,
    isLoading,
    filterByNameQuery,
    sort,
    sortBy,
    sortDir,
    reload
  } = usePlans(readPersistedPlanSort()) as {
    plans: PlanListSummary[];
    plansCount: number;
    plansNameQuery?: string;
    pageNo: number;
    goToPage: (page: number) => void;
    isLoading: boolean;
    filterByNameQuery: (query: string) => void;
    sort: (field: string, dir: string) => void;
    sortBy: string;
    sortDir: string;
    reload: () => void;
  };

  // FR-085: the save-confirmation toast's "Download PDF" button is fired
  // from `usePlanWizardMutations` (mounted only while the wizard is up) —
  // its real capability only exists here, once the list has mounted, so
  // it's registered on a bridge the toast calls into (mirrors Invoices'
  // own `useInvoiceActions()`/`registerInvoicePrinter` wiring exactly).
  const { handleAction, downloadingPDF } = usePlanEllipsisOptions(
    undefined
  ) as {
    handleAction: (action: string, id?: string) => void;
    downloadingPDF: boolean;
  };
  const handleActionRef = useRef(handleAction);
  handleActionRef.current = handleAction;
  useEffect(() => {
    setPrintListenerReady(true);
    registerPlanPrinter(planId =>
      handleActionRef.current('downloadPDFEnglish', planId)
    );
    return () => {
      setPrintListenerReady(false);
      registerPlanPrinter(null);
    };
  }, []);

  // FR-069: mirrors Applications/Work Orders' `OperationsPage` "No Crop
  // Zones Found" tier — distinct from the zero-Plans (FR-063) and
  // zero-search/filter-results (FR-065) empty states.
  const {
    state: { cropSeasons, loadingOrg, isGlobalCropSeasonLoading }
  } = useStore();
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  const hasCropZonesInCropSeasons = useMemo(
    () =>
      ((cropSeasons as { id: string; cropZoneIds?: string[] }[]) || []).some(
        cropSeason =>
          selectedCropSeasonIds.includes(cropSeason.id) &&
          (cropSeason.cropZoneIds?.length ?? 0) > 0
      ),
    [cropSeasons, selectedCropSeasonIds]
  );

  // null until the first Plans fetch settles, then whether the latest settled
  // fetch was an unsearched, empty result.
  const [lastSettledWasEmpty, setLastSettledWasEmpty] = useState<
    boolean | null
  >(null);
  const [searchText, setSearchText] = useState(
    () => new URLSearchParams(location.search).get('search') ?? ''
  );
  const debouncedSearchTerm = useDebounce(searchText, 300);
  React.useEffect(() => {
    filterByNameQuery(debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const [filters, setFilters] = useState<PlanFilters>(PLAN_FILTER_DEFAULTS);
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);
  const hasActiveFilters = useMemo(
    () => filters.creationDateRange.length === 2,
    [filters.creationDateRange]
  );

  // Client-side only (FR-031): filters the current page's already-fetched
  // `plans` by `createdOn`, reusing legacy Plans' own predicate as-is — no
  // new query parameter is ever sent to the Plans API.
  const filteredPlans = useMemo(() => {
    if (!hasActiveFilters) return dateRangeFilter(plans, []);
    const [start, end] = filters.creationDateRange as [
      moment.Moment,
      moment.Moment
    ];
    return dateRangeFilter(plans, [start.toDate(), end.toDate()]);
  }, [plans, filters, hasActiveFilters]);

  const deepLinkPlanId =
    useDeepLinkSelection('selectedPlanId') ??
    new URLSearchParams(location.search).get('planId');
  const [hasEditedDeepLinkSearch, setHasEditedDeepLinkSearch] = useState(false);
  const [
    deepLinkedPlanSummary,
    setDeepLinkedPlanSummary
  ] = useState<PlanListSummary | null>(null);
  const activeDeepLinkPlanId = hasEditedDeepLinkSearch ? null : deepLinkPlanId;
  const { selectedId, handleSelect, handleItemDeleted } = useListViewSelection({
    items: filteredPlans,
    isLoading,
    preferredId: activeDeepLinkPlanId
  });
  const visiblePlans = useMemo((): PlanListSummary[] => {
    if (!activeDeepLinkPlanId) return filteredPlans;
    const linkedPlan = filteredPlans.find(
      (plan: PlanListSummary) => plan.id === activeDeepLinkPlanId
    );
    if (linkedPlan) return [linkedPlan];
    if (deepLinkedPlanSummary?.id === activeDeepLinkPlanId) {
      return [deepLinkedPlanSummary];
    }
    return [];
  }, [filteredPlans, activeDeepLinkPlanId, deepLinkedPlanSummary]);

  const handleDeepLinkedPlanLoaded = useCallback(
    (plan: PlanDetails) => {
      if (plan.id !== deepLinkPlanId || !plan.name) return;
      setDeepLinkedPlanSummary({
        id: plan.id,
        name: plan.name,
        estimatedAreaValue: plan.estimatedAreaValue,
        estimatedAreaUnit: plan.estimatedAreaUnit
      });
    },
    [deepLinkPlanId]
  );

  const handleSearchChange = (value: string) => {
    setSearchText(value);

    const searchParams = new URLSearchParams(location.search);
    const hadRecordSourceParams =
      searchParams.has('search') || searchParams.has('planId');
    searchParams.delete('search');
    searchParams.delete('planId');

    if (hadRecordSourceParams) {
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : '',
        hash: location.hash,
        state: undefined
      });
    }

    setHasEditedDeepLinkSearch(true);
    message.destroy(RECORD_SOURCE_MESSAGE_KEY);
  };

  const handlePlanDeleted = useCallback(
    (deletedId: string) => handleItemDeleted(deletedId, reload),
    [handleItemDeleted, reload]
  );

  const { rowSelection, columns } = usePlanListViewColumns({
    selectedId,
    onSelectId: handleSelect,
    searchTerm: searchText
  });

  const handleSortChange = useCallback(
    (field: string, dir: 'asc' | 'desc') => {
      persistPlanSort(field, dir);
      sort(field, dir);
    },
    [sort]
  );

  const handleFilterClick = useCallback(
    () => setIsFilterDrawerVisible(true),
    []
  );

  // FR-075: matches the shared `usePagination`'s own showTotal format
  // (SummaryParamsContext.tsx) — Plans' search/filter state is local, not
  // that hook's `useSummaryParams()`, so it's replicated here rather than
  // reused directly.
  const pagination = useMemo(
    () => ({
      current: pageNo + 1,
      total: activeDeepLinkPlanId ? visiblePlans.length : plansCount,
      pageSize: PAGE_SIZE,
      showTotal: (total: number, range: number[]) =>
        `${range[0]}-${range[1]} of ${total}${
          hasActiveFilters || searchText.trim() ? ' results' : ''
        }`,
      showSizeChanger: false,
      onChange: (page: number) => goToPage(page - 1)
    }),
    [
      pageNo,
      plansCount,
      goToPage,
      hasActiveFilters,
      searchText,
      activeDeepLinkPlanId,
      visiblePlans.length
    ]
  );

  // `plans` can still hold a searched result after the search box is cleared,
  // until the unsearched refetch settles, so only that result proves an empty org.
  const plansAreUnsearched = !plansNameQuery?.trim();
  if (!isLoading) {
    const settledEmpty = plans.length === 0 && plansAreUnsearched;
    if (settledEmpty !== lastSettledWasEmpty) {
      setLastSettledWasEmpty(settledEmpty);
    }
  }
  const hasSearchOrFilter = Boolean(searchText.trim()) || hasActiveFilters;

  // FR-074: org/crop-season data itself is still loading — takes precedence
  // over every other tier below, matching Applications/Work Orders'
  // `OperationsPage`'s own `loadingOrg || isGlobalCropSeasonLoading`
  // short-circuit. Without this, `usePlans()` silently skips its reload
  // effect during that same window, and `plans=[]` would otherwise render
  // as a false "no Plans" empty state. The first Plans fetch also spins, so
  // the list shell doesn't flash before an empty org's empty state; later
  // reloads (search, sort, delete) keep the list mounted.
  if (loadingOrg || isGlobalCropSeasonLoading || lastSettledWasEmpty === null) {
    return buildLandingPage(
      <div className="flex items-center justify-center flex-1">
        <LoadingSpinner
          message="Loading..."
          className="flex items-center justify-center w-60 py-8 px-4 rounded-lg bg-white shadow-md"
        />
      </div>,
      {
        breadcrumbItems: [{ title: 'Plans', value: 'All Plans', isLast: true }]
      }
    );
  }

  // FR-069: no crop zones in the currently selected crop season(s) at all —
  // takes precedence over the zero-Plans check below, matching
  // Applications/Work Orders' own tier ordering.
  if (
    !isLoading &&
    (!hasCropZonesInCropSeasons || selectedCropSeasonIds.length === 0)
  ) {
    return buildLandingPage(
      <div className="pt-4 px-4">
        <div className="bg-white shadow-sm-card rounded-lg">
          <EmptyTableState
            title="No Crop Zones Found"
            message="Create a crop zone to proceed with adding Plans."
            overlayIcon={NoCropZonesFoundIcon}
            button={
              <Button
                type={ButtonType.primary}
                size="large"
                onClick={() => history.push(paths.properties)}
              >
                Go to Properties
              </Button>
            }
          />
        </div>
      </div>,
      {
        breadcrumbItems: [{ title: 'Plans', value: 'All Plans', isLast: true }]
      }
    );
  }

  // FR-063: a genuinely empty org (no active search/filter) gets an
  // illustrated, call-to-action-driven card in place of the whole list —
  // matching Applications/Work Orders' `OperationsPage` tiered strategy —
  // rather than the plain `emptyMessage` text `ListView` renders for the
  // filtered/searched-to-zero case below. `usePlans` fires overlapping
  // fetches on mount, so a reload after an empty result keeps this card up
  // instead of flashing the list shell in between.
  if (
    (!isLoading || lastSettledWasEmpty) &&
    filteredPlans.length === 0 &&
    !hasSearchOrFilter &&
    plansAreUnsearched
  ) {
    return buildLandingPage(
      <div className="pt-4 px-4">
        <div className="bg-white shadow-sm-card rounded-lg">
          <EmptyTableState
            title="No Plans Found"
            message="Create a Plan or change the selected season."
            overlayIcon={NoPlansFoundIcon}
            button={
              <Button
                type={ButtonType.primary}
                size="large"
                icon={<PlusIcon />}
                onClick={onCreateClick}
              >
                Create New
              </Button>
            }
          />
        </div>
      </div>,
      {
        breadcrumbItems: [{ title: 'Plans', value: 'All Plans', isLast: true }]
      }
    );
  }

  return buildLandingPage(
    <>
      <ListView
        className="plans-list-view"
        icon={<PlansRecordIcon />}
        title="Plans"
        searchPlaceholder="Search plans"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        sortDropdown={
          <PlanSortDropdown
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={handleSortChange}
          />
        }
        createButtonLabel="Create New"
        onCreateClick={onCreateClick}
        filterButtonLabel={FILTER_LABEL}
        onFilterClick={handleFilterClick}
        hasActiveFilters={hasActiveFilters}
        dataSource={visiblePlans}
        columns={columns}
        rowKey="id"
        rowSelection={rowSelection}
        pagination={pagination}
        onRowClick={record => handleSelect(record.id)}
        isLoading={isLoading}
        emptyMessage="No Plans found."
        detailPanel={
          <PlanDetailPanel
            planId={selectedId}
            onPlanLoaded={handleDeepLinkedPlanLoaded}
            isLoading={isLoading}
            onPlanDeleted={handlePlanDeleted}
            showSearchEmptyState={
              Boolean(searchText.trim()) && filteredPlans.length === 0
            }
            showFilterEmptyState={
              hasActiveFilters && filteredPlans.length === 0
            }
            onAdjustFilters={() => setIsFilterDrawerVisible(true)}
            onClearFilters={() => setFilters(PLAN_FILTER_DEFAULTS)}
          />
        }
        filterDrawer={
          <PlanFilterDrawer
            visible={isFilterDrawerVisible}
            initialValues={filters}
            showPaginationWarning={plansCount > 50}
            onClose={() => setIsFilterDrawerVisible(false)}
            onFiltersChange={setFilters}
          />
        }
      />
      <SpinnerModal open={downloadingPDF} message="Downloading PDF..." />
    </>,
    {
      breadcrumbItems: [{ title: 'Plans', value: 'All Plans', isLast: true }]
    }
  );
};

/**
 * Entry point for the redesigned Plans experience. "Create New" routes
 * to `${paths.plans}/create` rather than an inline toggle, matching
 * Applications/Work Orders' convention. Wrapped in SummaryParamsProvider
 * only because the shared DetailPanel requires it unconditionally —
 * Plans' own state comes from usePlans() instead.
 */
const PlansPage: React.FC = () => {
  const history = useHistory();
  useDismissNotificationOnNavigation(paths.plans, dismissPlanNotification);
  const {
    state: { organization }
  } = useStore();

  return (
    <div className="w-full h-full" data-testid="plans-redesign">
      <SummaryParamsProvider>
        <PlansListView
          key={organization?.id}
          onCreateClick={() => history.push(`${paths.plans}/create`)}
        />
      </SummaryParamsProvider>
    </div>
  );
};

export default PlansPage;
